import loadable from '@loadable/component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearDebouncedCloseRubric, closeDropdown, debouncedCloseRubric } from '../../actions/dropdown';
import { closeMenu } from '../../actions/menu';
import { Dropdown } from '../../components/Dropdown';
import { LoadableErrorBoundary } from '../../components/LoadableErrorBoundary';
import { LoadableFallback } from '../../components/LoadableFallback';
import { selectDropdownOpen } from '../../selectors/dropdown';
import { IApplicationState } from '../../types/redux';
import { ERubric } from '../../types/rubricsLinks';
import { useApplicationContext } from '../../utils/applicationContext';

const DropdownInnerContainerLoadable = loadable(
  () => import(/* webpackChunkName: "NavBarDropdownInner" */ './DropdownInnerContainerLoadable'),
  {
    fallback: <LoadableFallback />,
    ssr: false,
  },
);

interface IDropdownContainerProps {
  menuRef: React.RefObject<HTMLButtonElement | null>;
  navbarRef: React.RefObject<HTMLDivElement | null>;
}

export const DropdownContainer: React.FC<IDropdownContainerProps> = ({ menuRef, navbarRef }) => {
  const { logger } = useApplicationContext();
  const dispatch = useDispatch();
  const open = useSelector(selectDropdownOpen);
  const hoveredRubric = useSelector((state: IApplicationState) => state.dropdown.hoveredRubric);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const handleRetry = React.useCallback(() => {
    DropdownInnerContainerLoadable.load();
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    dispatch(debouncedCloseRubric());
  }, [dispatch]);

  const handleMouseEnter = React.useCallback(() => {
    dispatch(clearDebouncedCloseRubric());
  }, [dispatch]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const isClickOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      const isClickOutsideMenu = menuRef && menuRef.current && !menuRef.current.contains(target);
      const isClickOutsideNavbar = navbarRef && navbarRef.current && !navbarRef.current.contains(target);

      if (isClickOutsideDropdown && isClickOutsideMenu && isClickOutsideNavbar) {
        dispatch(closeDropdown());
        dispatch(closeMenu());
      }
    }
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dispatch, menuRef]);

  const isGrowDropdown = ERubric.Extra1093 === hoveredRubric;

  if (!open || !hoveredRubric) {
    return null;
  }

  return (
    <div ref={dropdownRef}>
      <Dropdown isGrow={isGrowDropdown} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
        <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
          <DropdownInnerContainerLoadable rubric={hoveredRubric} />
        </LoadableErrorBoundary>
      </Dropdown>
    </div>
  );
};
